const FREQUENCY_WORDS = {
  month: 'month|か月|ヶ月|mois|monat|maand|miesi|mensil|mensual|mensal|mese|mesi|meses|mês|mes\\b',
  week: 'week|weke|週間|semaine|woche|wochen|settiman|semanas|semana|tygod|tydzi|tyg\\.|semanal|wekelijks',
  day: 'day|日ごと|jour|tage|tag\\b|giorn|día|dias|dag\\b|dagen|dzie|dni\\b|dia\\b',
};
let pdpIntervalContextId = 0;

export function parseWipesPlanFrequency(rawName) {
  const name = String(rawName ?? '')
    .toLowerCase()
    .replace(/[０-９]/g, (digit) => String.fromCharCode(digit.charCodeAt(0) - 0xFEE0));
  let best = null;
  for (const unit of ['month', 'week', 'day']) {
    for (const match of name.matchAll(new RegExp(`(\\d+)\\s*(?:${FREQUENCY_WORDS[unit]})`, 'g'))) {
      if (!best || match.index > best.index) {
        best = { unit, count: Number.parseInt(match[1], 10), index: match.index };
      }
    }
  }
  if (best) return { unit: best.unit, count: best.count };
  for (const unit of ['month', 'week', 'day']) {
    if (new RegExp(FREQUENCY_WORDS[unit]).test(name)) return { unit, count: 1 };
  }
  return null;
}

export function parseWipesSubscriptionConfig(rawConfig) {
  const [unit = '', intervalList = '', defaultInterval = ''] = String(rawConfig ?? '').split('|');
  const intervals = [...new Set(
    intervalList.split(',').map(Number).filter((value) => Number.isSafeInteger(value) && value > 0),
  )];
  if (!unit || intervals.length === 0) return null;
  const normalizedDefault = Number(defaultInterval);
  return {
    unit,
    intervals,
    defaultInterval: intervals.includes(normalizedDefault) ? normalizedDefault : intervals[0],
  };
}

/**
 * Wipes-specific entitlement: a product-level plan is offered only when its
 * localized cadence matches the variant metafield configuration and Shopify
 * supplied an allocation for that exact variant/plan pair.
 */
export function resolveConfiguredPlanSet(model, variantId, selectedPlanId = null) {
  const variant = model.variantById(variantId);
  if (!variant) return { plans: [], selectedPlanId: null };
  const config = parseWipesSubscriptionConfig(variant.subscriptionConfig);
  if (!config) return { plans: [], selectedPlanId: null };

  const plans = config.intervals
    .map((interval) => {
      const matches = model.contract.plans.filter((plan) => {
        const frequency = parseWipesPlanFrequency(plan.name);
        return frequency?.unit === config.unit
          && frequency.count === interval
          && model.allocationFor(variant.id, plan.id);
      });
      return matches.length === 1 ? matches[0] : null;
    })
    .filter(Boolean);

  const retained = plans.find((plan) => plan.id === String(selectedPlanId ?? ''));
  const configuredDefault = plans.find((plan) => {
    const frequency = parseWipesPlanFrequency(plan.name);
    return frequency?.count === config.defaultInterval;
  });
  return {
    plans,
    selectedPlanId: retained?.id ?? configuredDefault?.id ?? plans[0]?.id ?? null,
  };
}

/** Existing Shopify allocations are the purchasable intervals; metafields retain defaults. */
export function resolveAvailablePlanSet(model, variantId, selectedPlanId = null) {
  const variant = model?.variantById(variantId);
  const config = parseWipesSubscriptionConfig(variant?.subscriptionConfig);
  if (!variant || !config) return { plans: [], selectedPlanId: null };
  const candidates = model.contract.plans.filter((plan) => {
    const frequency = parseWipesPlanFrequency(plan.name);
    return frequency?.unit === config.unit && frequency.count > 0
      && model.allocationFor(variant.id, plan.id);
  });
  // Duplicate cadences can have different terms. Never guess which one to offer.
  const plans = candidates.filter((plan) => candidates.filter((other) =>
    parseWipesPlanFrequency(other.name).count === parseWipesPlanFrequency(plan.name).count
  ).length === 1).sort((a, b) =>
    parseWipesPlanFrequency(a.name).count - parseWipesPlanFrequency(b.name).count);
  const retained = plans.find((plan) => plan.id === String(selectedPlanId ?? ''));
  const configuredDefault = plans.find((plan) =>
    parseWipesPlanFrequency(plan.name).count === config.defaultInterval);
  return { plans, selectedPlanId: retained?.id ?? configuredDefault?.id ?? plans[0]?.id ?? null };
}

/** Native select keeps long interval lists compact and supports platform keyboard/touch controls. */
export function renderPdpIntervalSelect(buyBox, options, selected, onChange) {
  // Records an interval chosen on this page (select or "Use" advice), unlike a plan from a link.
  const choose = (value) => { buyBox.pogiIntervalChosen = true; onChange(value); };
  buyBox.pogiIntervalOptions = options.map((option) => ({ ...option }));
  buyBox.pogiIntervalChange = choose;
  const wrap = buyBox.querySelector('[data-pogi-freq-opts]');
  if (!wrap) return;
  const focused = wrap.contains(wrap.ownerDocument.activeElement);
  wrap.replaceChildren();
  // This wrapper used to own radio buttons. The select owns its own semantics.
  wrap.removeAttribute('role');
  wrap.removeAttribute('aria-label');
  if (!options.length) return;
  const select = wrap.ownerDocument.createElement('select');
  select.dataset.pogiIntervalSelect = '';
  select.className = 'pogi-bb__interval-select';
  select.setAttribute('aria-label', buyBox.querySelector('.pogi-bb__subx-intervals-label')?.textContent.trim() || 'Delivery interval');
  for (const item of options) {
    const option = wrap.ownerDocument.createElement('option');
    option.value = String(item.value);
    option.textContent = item.label;
    select.append(option);
  }
  select.value = String(selected);
  select.addEventListener('change', () => choose(Number(select.value)), { signal: buyBox.v2Events.signal });
  wrap.append(select);
  if (focused) select.focus({ preventScroll: true });
}

/** Honor an explicit initial URL plan only for the exact selected variant/cadence. */
export function resolveInitialSellingPlanId(model, variantId, search, allAvailable = false) {
  if (!model?.variantById(variantId)) return null;
  const params = new URLSearchParams(String(search || ''));
  const plans = params.getAll('selling_plan');
  const variants = params.getAll('variant');
  if (plans.length !== 1 || !/^[1-9]\d*$/.test(plans[0])) return null;
  if (variants.length > 1 || (variants.length === 1 && variants[0] !== String(variantId))) return null;
  return (allAvailable ? resolveAvailablePlanSet : resolveConfiguredPlanSet)(model, variantId).plans
    .find((plan) => plan.id === plans[0])?.id ?? null;
}

// Compatibility export for the already-active Wipes rollout. New product
// migrations use the product-neutral name without disturbing that stable path.
export const resolveWipesPlanSet = resolveConfiguredPlanSet;

/** Shopper-entered daily use is an estimate, never a purchase or plan input. */
export function normalizeDailyUsage(value, fallback = 2) {
  const previous = Number.isFinite(Number(fallback)) ? Number(fallback) : 2;
  const number = value == null || String(value).trim() === '' ? previous : Number(value);
  return Math.min(100, Math.max(1, Math.round(Number.isFinite(number) ? number : previous)));
}

export function pdpUsageDuration(units, perDay, unit) {
  const divisor = { days: 1, weeks: 7, months: 30 }[unit];
  if (!(units > 0) || !(perDay > 0) || !divisor) return null;
  const duration = units / perDay / divisor;
  const precision = duration < 0.1 ? 100 : 10;
  return Math.round((duration + Number.EPSILON) * precision) / precision;
}

export function formatPdpUsageNumber(value, locale) {
  try { return new Intl.NumberFormat(locale || 'en', { maximumFractionDigits: 2 }).format(value); }
  catch { return new Intl.NumberFormat('en', { maximumFractionDigits: 2 }).format(value); }
}

/** Display units follow the unrounded supply; this never selects a pack or plan. */
export function pdpUsageEstimate(units, perDay, maxUnit = 'months') {
  if (!Number.isSafeInteger(units) || units <= 0 || !Number.isFinite(perDay)
    || perDay < 1 || perDay > 100 || !['days', 'weeks', 'months'].includes(maxUnit)) return null;
  const days = units / perDay;
  const unit = maxUnit === 'days' || days < 30 ? 'days' : maxUnit;
  const value = pdpUsageDuration(units, perDay, unit);
  return value > 0 ? { unit, value } : null;
}

export function formatPdpUsageDuration(estimate, locale) {
  if (!estimate) return '';
  const unit = { days: 'day', weeks: 'week', months: 'month' }[estimate.unit];
  if (!unit || !(estimate.value > 0)) return '';
  const options = { style: 'unit', unit, unitDisplay: 'long', maximumFractionDigits: 2 };
  try { return new Intl.NumberFormat(locale || 'en', options).format(estimate.value); }
  catch { return new Intl.NumberFormat('en', options).format(estimate.value); }
}

/** Reviewed locale copy is supplied by Liquid; missing opt-in markup keeps the legacy path. */
function pdpClarityCopy(buyBox) {
  const node = buyBox.querySelector('[data-pogi-clarity-copy]');
  if (!node) return null;
  try {
    const copy = JSON.parse(node.textContent);
    return copy?.version === 1 && typeof copy.example === 'string' ? copy : null;
  } catch { return null; }
}

/** Round display only; cadence recommendations continue to use exact supply days. */
export function pdpRoundedUsageEstimate(units, perDay) {
  if (!pdpUsageEstimate(units, perDay)) return null;
  const days = units / perDay;
  if (days < 1) return { unit: 'days', value: days };
  const unit = days < 30 ? 'days' : days < 56 ? 'weeks' : 'months';
  const precision = unit === 'months' ? 2 : 1;
  const value = Math.max(1, Math.round(days / { days: 1, weeks: 7, months: 30 }[unit] * precision) / precision);
  return { unit, value };
}

/** Approximation belongs to the selected weekly plan, never a calendar-month plan. */
export function pdpIntervalMonthContext(option, copy) {
  if (!option || option.months || !copy) return '';
  const frequency = parseWipesPlanFrequency(option.label);
  if (frequency?.unit !== 'week' || frequency.count < 8 || option.days !== frequency.count * 7) return '';
  return copy[`context_${frequency.count}`] || '';
}

/** Count tiles show unit prices; the selected variant's supply is shown once. */
export function pdpUsagePriceText(buyBox, { price, unit, cents }) {
  const panel = buyBox.querySelector('[data-pogi-usage-estimator]');
  const template = cents
    ? panel?.dataset.usageCentsTemplate || '[[price]]¢/[[unit]]'
    : panel?.dataset.usageMoneyTemplate || '[[price]]/[[unit]]';
  if (!['price', 'unit'].every(key => template.includes(`[[${key}]]`)) || template.includes('[[duration]]')) return '';
  return template.split('[[price]]').join(price).split('[[unit]]').join(unit);
}

export function bindPdpUsage(buyBox) {
  const input = buyBox.querySelector('[data-pogi-daily-use]');
  const signal = buyBox.v2Events.signal;
  // The next normal buy-box update supplies fresh context after a reconnect.
  buyBox.pogiUsageState = null;
  const commit = () => {
    if (!input || !buyBox.pogiUsageState) return;
    const perDay = normalizeDailyUsage(input.value, buyBox.pogiUsageState.perDay);
    // Empty/invalid input falls back safely without turning an example into a claimed routine.
    if (String(input.value).trim() && Number.isFinite(Number(input.value))) buyBox.pogiUsageEdited = true;
    buyBox.usagePerDay = perDay;
    input.value = String(perDay);
    // A display-only edit must not run the purchase/plan synchronization path.
    renderPdpUsage(buyBox, { ...buyBox.pogiUsageState, perDay });
  };
  input?.addEventListener('change', commit, { signal });
  input?.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' || event.isComposing) return;
    event.preventDefault();
    commit();
  }, { signal });
  const details = buyBox.querySelector('[data-pogi-usage-details]');
  if (details && buyBox.pogiUsageOpen !== undefined) details.open = buyBox.pogiUsageOpen;
  details?.addEventListener('toggle', () => { buyBox.pogiUsageOpen = details.open; }, { signal });
}

/** Explicit planning inputs, not a recommended cleaning frequency. */
export function wipesRoutineEstimate(values) {
  const result = {};
  for (const key of ['dogs', 'cleanups', 'wipes']) {
    const raw = values?.[key];
    const value = raw == null || String(raw).trim() === '' ? NaN : Number(raw);
    if (!Number.isInteger(value) || value < 1 || value > 10) return null;
    result[key] = value;
  }
  result.daily = result.dogs * result.cleanups * result.wipes;
  return result.daily <= 100 ? result : null;
}

/** Separate one selected variant's supply from the explicitly labeled order total. */
export function renderPdpUsage(buyBox, state) {
  const { enabled, unitsPerPack, quantity, perDay, subscribed, locale, maxUnit } = state;
  buyBox.pogiUsageState = { ...state };
  renderPdpIntervalAdvice(buyBox, state);
  const panel = buyBox.querySelector('[data-pogi-usage-estimator]');
  if (!panel) return;
  const copy = pdpClarityCopy(buyBox);
  if (copy) {
    renderPdpClarityUsage(buyBox, panel, state, copy);
    return;
  }
  const template = panel.dataset.usageTemplate || '';
  const unit = maxUnit || panel.dataset.usageUnit;
  const estimate = pdpUsageEstimate(unitsPerPack, perDay, unit);
  const valid = enabled && Number.isSafeInteger(unitsPerPack) && unitsPerPack > 0
    && estimate
    && ['units', 'duration'].every((key) => template.includes(`[[${key}]]`));
  const durationText = formatPdpUsageDuration(estimate, locale);
  const summary = valid ? template
    .split('[[units]]').join(formatPdpUsageNumber(unitsPerPack, locale))
    .split('[[duration]]').join(durationText) : '';
  const output = panel.querySelector('[data-pogi-usage-summary]');
  if (output) {
    // Live region: rewrite only on change so identical text is not re-announced.
    if (output.textContent !== summary) output.textContent = summary;
    output.hidden = !summary;
  }
  // Unavailable selections can still use the calculator to find another pack.
  panel.hidden = !summary && !panel.querySelector('[data-pogi-sizing-open]');
  const monthNote = panel.querySelector('[data-pogi-usage-month-note]');
  if (monthNote) monthNote.hidden = !summary || estimate?.unit !== 'months';
  const assumption = panel.querySelector('[data-pogi-usage-assumption]');
  if (assumption) {
    assumption.textContent = summary ? (panel.dataset.usageAssumptionTemplate || '')
      .split('[[per_day]]').join(formatPdpUsageNumber(perDay, locale)) : '';
    assumption.hidden = !assumption.textContent;
  }
  const input = panel.querySelector('[data-pogi-daily-use]');
  if (input && input.ownerDocument.activeElement !== input) input.value = String(perDay);
  const note = panel.querySelector('[data-pogi-usage-mode-note]');
  if (note) note.hidden = !subscribed;
  const interval = buyBox.querySelector('[data-pogi-usage]');
  if (interval) {
    const units = unitsPerPack * quantity;
    const orderEstimate = Number.isSafeInteger(quantity) && quantity > 0 && Number.isSafeInteger(units)
      ? pdpUsageEstimate(units, perDay, unit) : null;
    const quantityLabel = panel.dataset.usageOrderQuantityLabel;
    interval.textContent = subscribed && summary && orderEstimate && quantityLabel
      ? `${quantityLabel}: ${formatPdpUsageNumber(quantity, locale)} · ${template
        .split('[[units]]').join(formatPdpUsageNumber(units, locale))
        .split('[[duration]]').join(formatPdpUsageDuration(orderEstimate, locale))}` : '';
    interval.hidden = !interval.textContent;
  }
}

/** One order-total estimate replaces the duplicated pack/order estimates in the English draft. */
function renderPdpClarityUsage(buyBox, panel, state, copy) {
  const { enabled, unitsPerPack, quantity, perDay, subscribed, locale } = state;
  const units = unitsPerPack * quantity;
  const validOrder = enabled && Number.isSafeInteger(unitsPerPack) && unitsPerPack > 0
    && Number.isSafeInteger(quantity) && quantity > 0 && Number.isSafeInteger(units);
  const estimate = validOrder ? pdpRoundedUsageEstimate(units, perDay) : null;
  const underOneDay = estimate?.unit === 'days' && estimate.value < 1;
  const template = underOneDay ? copy.under_day : quantity > 1 ? copy.total : panel.dataset.usageTemplate;
  const valid = estimate && (underOneDay ? ['units'] : ['units', 'duration'])
    .every((key) => template?.includes(`[[${key}]]`));
  const duration = formatPdpUsageDuration(estimate, locale);
  const summary = valid ? template.split('[[units]]').join(formatPdpUsageNumber(units, locale))
    .split('[[duration]]').join(duration) : '';
  const output = panel.querySelector('[data-pogi-usage-summary]');
  if (output) { if (output.textContent !== summary) output.textContent = summary; output.hidden = !summary; }
  panel.hidden = !summary && !panel.querySelector('[data-pogi-sizing-open]');
  const assumption = panel.querySelector('[data-pogi-usage-assumption]');
  if (assumption) {
    const assumptionKey = buyBox.pogiUsageEdited ? 'based_on' : 'example';
    const singularTemplate = copy[`${assumptionKey}_one`];
    const assumptionTemplate = perDay === 1 && typeof singularTemplate === 'string'
      ? singularTemplate : copy[assumptionKey];
    assumption.textContent = summary ? assumptionTemplate
      .split('[[per_day]]').join(formatPdpUsageNumber(perDay, locale)) : '';
    assumption.hidden = !assumption.textContent;
  }
  const input = panel.querySelector('[data-pogi-daily-use]');
  if (input && input.ownerDocument.activeElement !== input) input.value = String(perDay);
  const monthNote = panel.querySelector('[data-pogi-usage-month-note]');
  if (monthNote) monthNote.hidden = !summary || estimate?.unit !== 'months';
  const note = panel.querySelector('[data-pogi-usage-mode-note]');
  if (note) note.hidden = true;
  const interval = buyBox.querySelector('[data-pogi-usage]');
  const select = buyBox.querySelector('[data-pogi-interval-select]');
  const selected = buyBox.pogiIntervalOptions?.find((option) => String(option.value) === String(select?.value));
  if (interval) {
    interval.textContent = subscribed && enabled ? pdpIntervalMonthContext(selected, copy) : '';
    interval.hidden = !interval.textContent;
    // Buy-box hosts do not always have an ID. Keep the helper association stable and unique.
    if (!interval.id) interval.id = buyBox.id ? `${buyBox.id}-interval-context` : `pogi-interval-context-${++pdpIntervalContextId}`;
    if (interval.textContent) select?.setAttribute('aria-describedby', interval.id);
    else select?.removeAttribute('aria-describedby');
  }
}

/** An example calculation only: never chooses a selling plan or delivery interval. */
export function renderSubscriptionUsage(element, { enabled, unitsPerPack, quantity = 1, perDay, durationUnit = 'days', template, locale }) {
  if (!element) return;
  const units = unitsPerPack * quantity;
  let text = '';
  if (enabled && Number.isSafeInteger(unitsPerPack) && unitsPerPack > 0
    && Number.isSafeInteger(quantity) && quantity > 0 && Number.isSafeInteger(units)
    && Number.isFinite(perDay) && perDay > 0
    && ['days', 'weeks'].includes(durationUnit)
    && ['per_day', 'units', durationUnit].every((key) => String(template || '').includes(`[[${key}]]`))) {
    let numbers;
    try { numbers = new Intl.NumberFormat(locale || 'en', { maximumFractionDigits: 2 }); }
    catch { numbers = new Intl.NumberFormat('en', { maximumFractionDigits: 2 }); }
    text = template;
    const duration = durationUnit === 'weeks' ? Math.round(units / perDay / 7) : units / perDay;
    for (const [key, value] of Object.entries({ per_day: perDay, units, [durationUnit]: duration })) {
      text = text.split(`[[${key}]]`).join(numbers.format(value));
    }
  }
  element.textContent = text;
  element.hidden = !text;
}

/** The builder pins one variant per row. Read its actual count option, not merchant card copy. */
export function subscriptionUsageForProduct(handle, options) {
  const family = {
    'pogis-dog-wipes': 'wipes',
    'pogis-dog-training-pads': 'pads',
    'pogis-dog-poop-bags': 'bags',
    'pogis-compostable-dog-poop-bags': 'bags',
  }[handle];
  if (!family || !Array.isArray(options)) return null;
  const candidates = options.map((option) => {
    const normalized = String(option).replace(/[０-９]/g, (digit) => String.fromCharCode(digit.charCodeAt(0) - 0xFEE0));
    // Current packs have ungrouped whole counts. Ambiguous decimal/thousands
    // punctuation must suppress the example rather than silently change its math.
    if (/\d[.,，．]\d|\d[ \u00a0\u202f]+\d{3}\b/.test(normalized)) return null;
    const numbers = (normalized.match(/\d+/g) || []).map(Number);
    // Pad dimensions contain two numbers; a rolls/bags count intentionally does too.
    if (numbers.length !== 1 && !(family === 'bags' && numbers.length === 2)) return null;
    return Math.max(...numbers);
  }).filter((count) => Number.isSafeInteger(count) && count > 0);
  if (candidates.length !== 1) return null;
  return { family, unitsPerPack: candidates[0], perDay: family === 'wipes' ? 4 : 2 };
}

/** Advisory only. Calendar months are approximated as 30 days; no purchase state is changed. */
export function resolvePdpIntervalAdvice(options, { unitsPerPack, quantity, perDay }) {
  if (!Number.isSafeInteger(unitsPerPack) || unitsPerPack <= 0
    || !Number.isSafeInteger(quantity) || quantity <= 0
    || !Number.isSafeInteger(unitsPerPack * quantity)
    || !Number.isFinite(perDay) || perDay < 1 || perDay > 100) return null;
  const candidates = (options || []).filter((item) => Number.isFinite(item.days) && item.days > 0
    && Number.isSafeInteger(Number(item.value)) && Number(item.value) > 0 && item.label);
  const sorted = candidates.filter((item) => candidates.filter((other) => other.days === item.days).length === 1)
    .sort((a, b) => a.days - b.days);
  if (!sorted.length) return null;
  const supplyDays = unitsPerPack * quantity / perDay;
  const fitting = sorted.filter((item) => item.days <= supplyDays);
  const option = fitting.at(-1) || null;
  return { supplyDays, option, status: !option ? 'too-short' : supplyDays > sorted.at(-1).days ? 'longest' : 'suggested' };
}

export function renderPdpIntervalAdvice(buyBox, state) {
  const panel = buyBox.querySelector('[data-pogi-interval-advice]');
  if (!panel) return;
  const nodes = [];
  // The panel is a status region: replace its content only when the advice
  // changes so assistive technology announces each new advice once.
  const commit = () => {
    const signature = nodes.map((node) => `${node.tagName || node.tag}:${node.textContent}`).join('\n');
    const signal = buyBox.v2Events?.signal;
    if (panel.pogiAdviceSignature === signature && panel.pogiAdviceSignal === signal) return;
    panel.pogiAdviceSignature = signature;
    panel.pogiAdviceSignal = signal;
    panel.replaceChildren();
    panel.append(...nodes);
    panel.hidden = nodes.length === 0;
  };
  if (!state.enabled || !state.subscribed || state.recommendationAllowed === false) return commit();
  const advice = resolvePdpIntervalAdvice(buyBox.pogiIntervalOptions, state);
  if (!advice) return commit();
  const doc = panel.ownerDocument;
  const selectedValue = buyBox.querySelector('[data-pogi-interval-select]')?.value;
  const selected = buyBox.pogiIntervalOptions.find((item) => String(item.value) === String(selectedValue));
  if (!selected) return commit();
  // Until the shopper chooses an interval here or enters their own use, the
  // estimate is only the page's example: never argue with a preselected plan
  // (default or linked), and show advice only when it agrees with it.
  const personalized = buyBox.pogiUsageEdited || buyBox.pogiIntervalChosen;
  if (!personalized && String(advice.option?.value) !== String(selectedValue)) return commit();
  const copy = pdpClarityCopy(buyBox);
  const title = doc.createElement('p');
  title.className = 'pogi-bb__interval-advice-title';
  if (!advice.option) {
    title.textContent = copy?.too_short || 'At this use, your supply may run out before the shortest available interval. Try a larger order quantity or a different pack.';
  } else {
    const same = String(advice.option.value) === String(selectedValue);
    const label = advice.status === 'longest' ? 'Longest available interval' : 'Suggested interval';
    title.textContent = `${label}: ${advice.option.label}${same ? ' (selected)' : ''}`;
    if (copy) {
      title.textContent = same && advice.status !== 'longest' ? copy.match
        : (advice.status === 'longest' ? copy.longest : copy.suggested).split('[[interval]]').join(advice.option.label);
      if (same && advice.status === 'longest') title.textContent += copy.selected;
    }
    if (selected?.days > advice.supplyDays) {
      const warning = doc.createElement('p');
      warning.textContent = copy?.warning || 'Your selected interval is longer than the estimated supply.';
      nodes.push(warning);
    }
    nodes.push(title);
    if (!same && typeof buyBox.pogiIntervalChange === 'function') {
      const action = doc.createElement('button');
      action.type = 'button';
      action.className = 'pogi-bb__interval-apply';
      action.textContent = copy ? copy.apply.split('[[interval]]').join(advice.option.label) : `Use ${advice.option.label}`;
      action.addEventListener('click', () => {
        buyBox.pogiIntervalChange(Number(advice.option.value));
        buyBox.querySelector('[data-pogi-interval-select]')?.focus({ preventScroll: true });
      }, { signal: buyBox.v2Events.signal });
      nodes.push(action);
    }
  }
  if (!advice.option) nodes.push(title);
  const note = doc.createElement('p');
  note.className = 'pogi-bb__interval-advice-note';
  note.textContent = 'Based on your daily-use estimate. Delivery transit time is not included.';
  if (buyBox.pogiIntervalOptions.some((option) => option.months)) note.textContent += ' Months are estimated at 30 days.';
  if (copy) {
    note.textContent = copy.advice_note;
    if (buyBox.pogiIntervalOptions.some((option) => option.months)) note.textContent += ` ${copy.months_basis}`;
  }
  nodes.push(note);
  commit();
}
